import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import CategoriesManager from './CategoriesManager';
import LoadingSpinner from './LoadingSpinner';
import ProductImportModal from './ProductImportModal';
import ProductTypeSelector from './ProductTypeSelector';
import GasDistributorProductForm from './GasDistributorProductForm';
import './ProductsManager.css';
import AppIcon from './icons/AppIcon';

const ProductsManager = () => {
  const { user } = useAuth();
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'grid';
    return localStorage.getItem('productsViewMode') || 'grid';
  });
  const [filtros, setFiltros] = useState({
    busca: '',
    categoria: '',
    status: 'ATIVO'
  });
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showCategoriaModal, setShowCategoriaModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [tipoProdutoSelecionado, setTipoProdutoSelecionado] = useState(null);
  const [produtoEditando, setProdutoEditando] = useState(null);
  const [produtoGasDistributor, setProdutoGasDistributor] = useState(null);
  const [gasDistributorProjectId, setGasDistributorProjectId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state
  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    descricao_curta: '',
    categoria_id: '',
    sku: '',
    preco_custo: '',
    preco_venda: '',
    preco_promocional: '',
    estoque_atual: 0,
    estoque_minimo: 0,
    unidade_medida: 'UN',
    status: 'ATIVO',
    destaque: false,
    promocao: false,
    tags: [],
    imagem_principal: '',
    imagens: []  // ✅ Suporte a múltiplas imagens
  });
  
  // Limite máximo de imagens por produto
  const MAX_IMAGENS = 5;

  useEffect(() => {
    loadData();
  }, [user]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('productsViewMode', viewMode);
    }
  }, [viewMode]);

  // ✅ Recarregar produtos quando filtros mudam
  useEffect(() => {
    if (companyId) {
      loadProdutos(companyId);
    }
  }, [filtros]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Buscar company_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user?.id)
        .single();

      if (!profile?.company_id) {
        setError('Usuário não vinculado a empresa');
        return;
      }

      setCompanyId(profile.company_id);
      
      // Buscar projeto gas_distributor da empresa
      const { data: gasProject } = await supabase
        .from('projects')
        .select('id')
        .eq('company_id', profile.company_id)
        .eq('project_type', 'gas_distributor')
        .single();
      
      if (gasProject) {
        setGasDistributorProjectId(gasProject.id);
      }
      
      // Carregar categorias e produtos
      await Promise.all([
        loadCategorias(profile.company_id),
        loadProdutos(profile.company_id)
      ]);
      
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadCategorias = async (cid) => {
    const { data, error } = await supabase
      .from('categorias_produtos')
      .select('*')
      .eq('company_id', cid)
      .eq('ativo', true)
      .order('ordem', { ascending: true });

    if (!error && data) {
      setCategorias(data);
    }
  };

  const loadProdutos = async (cid) => {
    // 1. Buscar produtos gerais
    let queryGerais = supabase
      .from('produtos_empresa')
      .select('*, categorias_produtos(nome)')
      .eq('company_id', cid)
      .is('deleted_at', null);

    if (filtros.categoria) {
      queryGerais = queryGerais.eq('categoria_id', filtros.categoria);
    }
    
    if (filtros.status) {
      queryGerais = queryGerais.eq('status', filtros.status);
    }

    if (filtros.busca) {
      queryGerais = queryGerais.or(`nome.ilike.%${filtros.busca}%,descricao.ilike.%${filtros.busca}%`);
    }

    queryGerais = queryGerais.order('created_at', { ascending: false });

    const { data: produtosGerais } = await queryGerais;

    // 2. Buscar produtos de distribuidora
    let queryDistribuidora = supabase
      .from('gas_distributor_products')
      .select('*')
      .eq('company_id', cid);

    // Aplicar filtro de status (produtos da distribuidora usam campo 'ativo' boolean)
    if (filtros.status === 'ATIVO') {
      queryDistribuidora = queryDistribuidora.eq('ativo', true);
    } else if (filtros.status === 'INATIVO') {
      queryDistribuidora = queryDistribuidora.eq('ativo', false);
    }
    // Se filtros.status estiver vazio, não filtra (mostra todos)

    if (filtros.busca) {
      queryDistribuidora = queryDistribuidora.or(`nome.ilike.%${filtros.busca}%,descricao.ilike.%${filtros.busca}%`);
    }

    queryDistribuidora = queryDistribuidora.order('ordem_exibicao', { ascending: true });

    const { data: produtosDistribuidora } = await queryDistribuidora;

    // 3. Combinar e marcar tipo
    const produtosGeraisComTipo = (produtosGerais || []).map(p => ({
      ...p,
      tipo_produto: 'geral',
      categoria_nome: p.categorias_produtos?.nome || 'Sem categoria'
    }));

    const produtosDistribuidoraComTipo = (produtosDistribuidora || []).map(p => ({
      ...p,
      tipo_produto: 'gas_distributor',
      categoria_nome: 'Distribuidora Gás e Água',
      status: p.ativo ? 'ATIVO' : 'INATIVO',
      imagem_principal: null
    }));

    // 4. Combinar listas
    setProdutos([...produtosGeraisComTipo, ...produtosDistribuidoraComTipo]);
  };

  const handleNovoProduto = () => {
    // Abrir seletor de tipo de produto
    setShowTypeSelector(true);
    setTipoProdutoSelecionado(null);
    setProdutoEditando(null);
    setProdutoGasDistributor(null);
  };

  const handleSelectTipoProduto = (tipo) => {
    setTipoProdutoSelecionado(tipo);
    setShowTypeSelector(false);
    
    if (tipo === 'geral') {
      // Verificar se existem categorias cadastradas
      if (categorias.length === 0) {
        setError('⚠️ Você precisa criar pelo menos uma categoria antes de cadastrar produtos!');
        setTimeout(() => {
          setError(null);
          setShowCategoriaModal(true);
        }, 3000);
        return;
      }

      setForm({
        nome: '',
        descricao: '',
        descricao_curta: '',
        categoria_id: '',
        sku: '',
        preco_custo: '',
        preco_venda: '',
        preco_promocional: '',
        estoque_atual: 0,
        estoque_minimo: 0,
        unidade_medida: 'UN',
        status: 'ATIVO',
        destaque: false,
        promocao: false,
        tags: [],
        imagem_principal: '',
        imagens: []
      });
      setShowModal(true);
    } else if (tipo === 'gas_distributor') {
      // Abrir formulário específico de gas distributor
      setShowModal(true);
    }
  };

  // ✅ Upload de múltiplas imagens para Storage Supabase
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Verificar limite de imagens
    const imagensAtuais = form.imagens || [];
    if (imagensAtuais.length + files.length > MAX_IMAGENS) {
      setError(`Máximo de ${MAX_IMAGENS} imagens por produto`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB em bytes
    const novasImagens = [];
    let uploadCount = 0;
    let errorCount = 0;

    for (const file of files) {
      // Validar tamanho (5MB)
      if (file.size > maxSize) {
        setError(`A imagem "${file.name}" deve ter no máximo 5MB`);
        setTimeout(() => setError(null), 3000);
        errorCount++;
        continue;
      }

      // Validar tipo
      if (!file.type.startsWith('image/')) {
        setError(`"${file.name}" não é uma imagem válida`);
        setTimeout(() => setError(null), 3000);
        errorCount++;
        continue;
      }

      // ✅ NOVO: Upload para Storage Supabase
      try {
        const fileName = `${companyId}/${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${file.name}`;
        
        const { data, error: uploadError } = await supabase.storage
          .from('products-images')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error(`[ProductsManager] Erro ao fazer upload de ${file.name}:`, uploadError);
          setError(`Erro ao fazer upload de "${file.name}": ${uploadError.message}`);
          setTimeout(() => setError(null), 3000);
          errorCount++;
          continue;
        }

        // ✅ Obter URL pública
        const { data: { publicUrl } } = supabase.storage
          .from('products-images')
          .getPublicUrl(fileName);

        console.log(`[ProductsManager] ✅ Imagem ${file.name} salva em:`, publicUrl);
        novasImagens.push(publicUrl);
        uploadCount++;
      } catch (err) {
        console.error(`[ProductsManager] Erro ao processar "${file.name}":`, err);
        setError(`Erro ao processar "${file.name}": ${err.message}`);
        setTimeout(() => setError(null), 3000);
        errorCount++;
      }
    }

    if (novasImagens.length > 0) {
      const todasImagens = [...imagensAtuais, ...novasImagens];
      setForm({ 
        ...form, 
        imagens: todasImagens,
        imagem_principal: todasImagens[0] // Primeira imagem é a principal
      });
      
      setSuccess(`✅ ${uploadCount} imagem(ns) enviada(s) com sucesso!`);
      setTimeout(() => setSuccess(null), 3000);
    }
    
    if (errorCount > 0) {
      console.warn(`[ProductsManager] ${errorCount} erro(s) ao fazer upload`);
    }
    
    // Limpar input para permitir selecionar mesmos arquivos novamente
    e.target.value = '';
  };

  // ✅ Remover uma imagem específica (e do Storage)
  const handleRemoveImage = async (index) => {
    const imagemRemover = form.imagens[index];
    
    // ✅ Deletar do Storage se for URL pública
    if (imagemRemover && imagemRemover.startsWith('https://')) {
      try {
        // Extrair caminho da URL
        const urlParts = imagemRemover.split('/storage/v1/object/public/products-images/');
        if (urlParts.length === 2) {
          const filePath = urlParts[1];
          await supabase.storage
            .from('products-images')
            .remove([filePath]);
          console.log(`[ProductsManager] ✅ Imagem deletada do Storage: ${filePath}`);
        }
      } catch (err) {
        console.error(`[ProductsManager] Erro ao deletar imagem do Storage:`, err);
        // Continuar mesmo se falhar ao deletar do Storage
      }
    }
    
    const novasImagens = form.imagens.filter((_, i) => i !== index);
    setForm({
      ...form,
      imagens: novasImagens,
      imagem_principal: novasImagens[0] || ''
    });
  };

  // ✅ Definir imagem como principal (mover para primeira posição)
  const handleSetPrincipal = (index) => {
    if (index === 0) return; // Já é a principal
    const novasImagens = [...form.imagens];
    const [imagem] = novasImagens.splice(index, 1);
    novasImagens.unshift(imagem);
    setForm({
      ...form,
      imagens: novasImagens,
      imagem_principal: novasImagens[0]
    });
  };

  const handleEditarProduto = (produto) => {
    setProdutoEditando(produto);
    
    if (produto.tipo_produto === 'gas_distributor') {
      // ✅ Produto de distribuidora - abrir formulário específico
      console.log('[ProductsManager] Editando produto de distribuidora:', produto.id);
      setTipoProdutoSelecionado('gas_distributor');
      setProdutoGasDistributor(produto);
      setShowModal(true);
    } else {
      // ✅ Produto geral - abrir formulário padrão
      console.log('[ProductsManager] Editando produto geral:', produto.id);
      setTipoProdutoSelecionado('geral');
      
      // ✅ Priorizar campo 'imagens' (JSONB com URLs migradas)
      // Se não houver, montar a partir de imagem_principal + imagens_adicionais
      let imagens = [];
      
      if (produto.imagens && Array.isArray(produto.imagens) && produto.imagens.length > 0) {
        // ✅ Usar array JSONB (já contém todas as imagens migradas)
        imagens = produto.imagens.filter(img => img && typeof img === 'string');
      } else {
        // Fallback: montar a partir dos campos antigos (para produtos ainda não migrados)
        if (produto.imagem_principal && typeof produto.imagem_principal === 'string') {
          imagens.push(produto.imagem_principal);
        }
        if (produto.imagens_adicionais && Array.isArray(produto.imagens_adicionais)) {
          imagens.push(...produto.imagens_adicionais.filter(img => img && typeof img === 'string'));
        }
      }
      
      setForm({
        nome: produto.nome || '',
        descricao: produto.descricao || '',
        descricao_curta: produto.descricao_curta || '',
        categoria_id: produto.categoria_id || '',
        sku: produto.sku || '',
        preco_custo: produto.preco_custo || '',
        preco_venda: produto.preco_venda || '',
        preco_promocional: produto.preco_promocional || '',
        estoque_atual: produto.estoque_atual || 0,
        estoque_minimo: produto.estoque_minimo || 0,
        unidade_medida: produto.unidade_medida || 'UN',
        status: produto.status || 'ATIVO',
        destaque: produto.destaque || false,
        promocao: produto.promocao || false,
        tags: produto.tags || [],
        imagem_principal: imagens[0] || '',
        imagens: imagens
      });
      setShowModal(true);
    }
  };

  const handleSalvarProduto = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Se for gas_distributor, o GasDistributorProductForm já salvou
    if (tipoProdutoSelecionado === 'gas_distributor') {
      return;
    }

    try {
      // Gerar SKU automático se não fornecido
      let sku = form.sku?.trim();
      if (!sku && !produtoEditando) {
        // Gerar SKU baseado no timestamp + random
        sku = `PRD-${Date.now()}-${Math.random().toString(36).substr(2, 5).toUpperCase()}`;
      }

      // ✅ Preparar imagens: primeira é principal, restante são adicionais
      const imagens = form.imagens || [];
      const imagemPrincipal = imagens[0] || '';
      const imagensAdicionais = imagens.slice(1);

      // Preparar dados convertendo strings vazias para null em campos numéricos
      const dataToSave = {
        ...form,
        company_id: companyId,
        sku: sku || null,
        preco_custo: form.preco_custo === '' ? null : parseFloat(form.preco_custo),
        preco_venda: form.preco_venda === '' ? null : parseFloat(form.preco_venda),
        preco_promocional: form.preco_promocional === '' ? null : parseFloat(form.preco_promocional),
        estoque_atual: form.estoque_atual === '' ? null : parseInt(form.estoque_atual),
        estoque_minimo: form.estoque_minimo === '' ? 0 : parseInt(form.estoque_minimo),
        categoria_id: form.categoria_id === '' ? null : form.categoria_id,
        imagem_principal: imagemPrincipal,
        imagens_adicionais: imagensAdicionais.length > 0 ? imagensAdicionais : null,
        imagens: imagens.length > 0 ? imagens : null,  // Campo JSONB com todas
        created_by: produtoEditando ? undefined : user.id,
        updated_by: user.id
      };

      if (produtoEditando) {
        // Atualizar
        const { error: updateError } = await supabase
          .from('produtos_empresa')
          .update(dataToSave)
          .eq('id', produtoEditando.id);

        if (updateError) throw updateError;
        setSuccess('Produto atualizado com sucesso!');
      } else {
        // Criar
        const { error: insertError } = await supabase
          .from('produtos_empresa')
          .insert([dataToSave]);

        if (insertError) throw insertError;
        setSuccess('Produto criado com sucesso!');
      }

      setShowModal(false);
      setTipoProdutoSelecionado(null);
      loadProdutos(companyId);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Erro ao salvar produto:', err);
      setError(err.message || 'Erro ao salvar produto');
    }
  };

  const handleSalvarGasDistributor = (produtoSalvo) => {
    setSuccess('Produto de distribuidora salvo com sucesso!');
    setShowModal(false);
    setTipoProdutoSelecionado(null);
    setProdutoGasDistributor(null);
    loadProdutos(companyId);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleDeletarProduto = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este produto?')) return;

    try {
      const { error } = await supabase
        .from('produtos_empresa')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      
      setSuccess('Produto excluído com sucesso!');
      loadProdutos(companyId);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Erro ao excluir produto');
    }
  };

  const handleImportProduct = (data) => {
    setProdutoEditando(null);
    setForm({
      nome: data.nome || '',
      descricao: data.descricao || '',
      descricao_curta: data.descricao_curta || '',
      categoria_id: '',
      sku: '',
      preco_custo: '',
      preco_venda: data.preco || '',
      preco_promocional: '',
      estoque_atual: 0,
      estoque_minimo: 0,
      unidade_medida: 'UN',
      status: 'ATIVO',
      destaque: false,
      promocao: false,
      tags: [],
      imagem_principal: data.imagem_principal || '',
      imagens: data.imagens || []
    });
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="products-manager">
        <LoadingSpinner message="Carregando produtos..." />
      </div>
    );
  }

  return (
    <div className="products-manager">
      {/* Header */}
      <div className="products-header-bar">
        <div>
          <h2>📦 Gestão de Produtos</h2>
          <p>{produtos.length} produto(s) cadastrado(s) | {categorias.length} categoria(s)</p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="btn-icon view-toggle"
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            title={viewMode === 'grid' ? 'Visualizar em lista' : 'Visualizar em cards'}
          >
            <AppIcon name={viewMode === 'grid' ? 'view_list' : 'grid_view'} />
          </button>
          <button className="btn-secondary" onClick={() => setShowCategoriaModal(true)}>
            <AppIcon name="category" />
            Categorias
            {categorias.length === 0 && <span className="badge-alert">!</span>}
          </button>
          <button className="btn-secondary" onClick={() => setShowImportModal(true)}>
            <AppIcon name="link" />
            Importar URL
          </button>
          <button className="btn-primary" onClick={handleNovoProduto}>
            <AppIcon name="add" />
            Novo Produto
          </button>
        </div>
      </div>

      {/* Alerta de Categorias */}
      {categorias.length === 0 && (
        <div className="alert alert-warning">
          <AppIcon name="warning" />
          <div>
            <strong>Nenhuma categoria cadastrada!</strong>
            <p>Crie pelo menos uma categoria antes de cadastrar produtos. <button className="link-button" onClick={() => setShowCategoriaModal(true)}>Criar agora</button></p>
          </div>
        </div>
      )}

      {/* Mensagens */}
      {error && (
        <div className="alert alert-error">
          <AppIcon name="error" />
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <AppIcon name="check_circle" />
          {success}
        </div>
      )}

      {/* Filtros */}
      <div className="products-filters">
        <div className="filter-group">
          <AppIcon name="search" />
          <input
            type="text"
            placeholder="Buscar produtos..."
            value={filtros.busca}
            onChange={(e) => {
              setFiltros({ ...filtros, busca: e.target.value });
            }}
          />
        </div>
        
        <select
          value={filtros.categoria}
          onChange={(e) => {
            setFiltros({ ...filtros, categoria: e.target.value });
          }}
        >
          <option value="">Todas as categorias</option>
          {categorias.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.nome}</option>
          ))}
        </select>

        <select
          value={filtros.status}
          onChange={(e) => {
            setFiltros({ ...filtros, status: e.target.value });
          }}
        >
          <option value="">Todos os status</option>
          <option value="ATIVO">Ativo</option>
          <option value="INATIVO">Inativo</option>
          <option value="RASCUNHO">Rascunho</option>
          <option value="ESGOTADO">Esgotado</option>
        </select>
      </div>

      {/* Lista de Produtos */}
      {produtos.length === 0 ? (
        <div className="empty-state">
          <AppIcon name="inventory_2" />
          <h3>Nenhum produto cadastrado</h3>
          <p>Clique em "Novo Produto" para começar</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="products-grid">
          {produtos.map(produto => (
            <div key={produto.id} className="product-card" data-tipo={produto.tipo_produto}>
              <div className="product-image">
                {/* Badge de tipo de produto */}
                {produto.tipo_produto === 'gas_distributor' && (
                  <span className="badge badge-gas-distributor">
                    <AppIcon name="local_fire_department" />
                    Gás e Água
                  </span>
                )}
                
                {produto.imagem_principal ? (
                  <img src={produto.imagem_principal} alt={produto.nome} />
                ) : (
                  <div className="no-image">
                    <AppIcon name={produto.tipo_produto === 'gas_distributor' ? 'local_fire_department' : 'image'} />
                  </div>
                )}
                {produto.destaque && <span className="badge badge-destaque">Destaque</span>}
                {produto.promocao && <span className="badge badge-promocao">Promoção</span>}
                {/* ✅ Indicador de múltiplas imagens */}
                {((produto.imagens_adicionais && produto.imagens_adicionais.length > 0) || 
                  (produto.imagens && produto.imagens.length > 1)) && (
                  <span className="badge badge-images">
                    <AppIcon name="collections" />
                    {(produto.imagens?.length || (1 + (produto.imagens_adicionais?.length || 0)))}
                  </span>
                )}
              </div>
              
              <div className="product-info">
                <h3>{produto.nome}</h3>
                <div className="product-prices">
                  {produto.preco_promocional ? (
                    <>
                      <span className="price-old">R$ {Number(produto.preco_venda).toFixed(2)}</span>
                      <span className="price-promo">R$ {Number(produto.preco_promocional).toFixed(2)}</span>
                    </>
                  ) : (
                    <span className="price">R$ {Number(produto.preco_venda).toFixed(2)}</span>
                  )}
                </div>
                <div className="product-status">
                  <span className={`status-badge status-${produto.status.toLowerCase()}`}>
                    {produto.status}
                  </span>
                </div>
              </div>
              
              <div className="product-actions">
                <button className="btn-icon" onClick={() => handleEditarProduto(produto)} title="Editar">
                  <AppIcon name="edit" />
                </button>
                <button className="btn-icon btn-danger" onClick={() => handleDeletarProduto(produto.id)} title="Excluir">
                  <AppIcon name="delete" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="products-list">
          <div className="products-list-header">
            <span>Produto</span>
            <span>Categoria</span>
            <span>Preço</span>
            <span>Status</span>
            <span>Ações</span>
          </div>
          {produtos.map((produto) => (
            <div key={produto.id} className="products-list-row">
              <div className="products-list-main">
                <strong>{produto.nome}</strong>
                <span>{produto.descricao_curta || produto.descricao || '-'}</span>
              </div>
              <span>{produto.categoria_nome || 'Sem categoria'}</span>
              <span>
                {produto.preco_promocional ? (
                  <>
                    <span className="price-old">R$ {Number(produto.preco_venda).toFixed(2)}</span>
                    <span className="price-promo">R$ {Number(produto.preco_promocional).toFixed(2)}</span>
                  </>
                ) : (
                  <span className="price">R$ {Number(produto.preco_venda).toFixed(2)}</span>
                )}
              </span>
              <span className={`status-badge status-${produto.status.toLowerCase()}`}>
                {produto.status}
              </span>
              <div className="product-actions">
                <button className="btn-icon" onClick={() => handleEditarProduto(produto)} title="Editar">
                  <AppIcon name="edit" />
                </button>
                <button className="btn-icon btn-danger" onClick={() => handleDeletarProduto(produto.id)} title="Excluir">
                  <AppIcon name="delete" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Seleção de Tipo de Produto */}
      {showTypeSelector && (
        <div className="modal-overlay" onClick={() => setShowTypeSelector(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <ProductTypeSelector
              onSelect={handleSelectTipoProduto}
              onCancel={() => setShowTypeSelector(false)}
            />
          </div>
        </div>
      )}

      {/* Modal de Categorias */}
      {showCategoriaModal && (
        <div className="modal-overlay" onClick={() => setShowCategoriaModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <CategoriesManager
              companyId={companyId}
              onClose={() => setShowCategoriaModal(false)}
              onUpdate={() => loadCategorias(companyId)}
            />
          </div>
        </div>
      )}

      {/* Modal de Produto Geral */}
      {showModal && tipoProdutoSelecionado === 'geral' && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content product-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{produtoEditando ? 'Editar Produto' : 'Novo Produto'}</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <AppIcon name="close" />
              </button>
            </div>
            
            <form onSubmit={handleSalvarProduto} className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Nome do Produto *</label>
                  <input
                    type="text"
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>SKU</label>
                  <input
                    type="text"
                    value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  Descrição Curta
                  <span className="tooltip-icon" title="Usada pelos agentes de IA para listar produtos da categoria. Máximo de 40 palavras (aproximadamente 200 caracteres).">
                    <AppIcon name="info" />
                  </span>
                </label>
                <input
                  type="text"
                  value={form.descricao_curta}
                  onChange={(e) => setForm({ ...form, descricao_curta: e.target.value })}
                  placeholder="Breve descrição para listagens (máx. 40 palavras)"
                  maxLength="200"
                />
                <small>{form.descricao_curta.length}/200 caracteres</small>
              </div>

              <div className="form-group">
                <label>
                  Descrição Completa
                  <span className="tooltip-icon" title="Acessada pelos agentes ao buscar informações específicas deste produto. Sem limite de caracteres.">
                    <AppIcon name="info" />
                  </span>
                </label>
                <textarea
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  rows="4"
                  placeholder="Descrição detalhada do produto..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Categoria</label>
                  <select
                    value={form.categoria_id}
                    onChange={(e) => setForm({ ...form, categoria_id: e.target.value })}
                  >
                    <option value="">Selecione...</option>
                    {categorias.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nome}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="ATIVO">Ativo</option>
                    <option value="INATIVO">Inativo</option>
                    <option value="RASCUNHO">Rascunho</option>
                    <option value="ESGOTADO">Esgotado</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Preço de Venda *
                    <span className="tooltip-icon" title="Preço que será exibido para o cliente">
                      <AppIcon name="info" />
                    </span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.preco_venda}
                    onChange={(e) => setForm({ ...form, preco_venda: e.target.value })}
                    required
                    placeholder="0.00"
                  />
                </div>
                
                <div className="form-group">
                  <label>
                    Preço Promocional
                    <span className="tooltip-icon" title="Ativado quando configurada alguma promoção no sistema">
                      <AppIcon name="info" />
                    </span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.preco_promocional}
                    onChange={(e) => setForm({ ...form, preco_promocional: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Unidade de Medida</label>
                  <select
                    value={form.unidade_medida}
                    onChange={(e) => setForm({ ...form, unidade_medida: e.target.value })}
                  >
                    <option value="UN">Unidade</option>
                    <option value="KG">Quilograma</option>
                    <option value="L">Litro</option>
                    <option value="M">Metro</option>
                    <option value="CX">Caixa</option>
                    <option value="PCT">Pacote</option>
                    <option value="PORÇÃO">Porção</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>
                  Imagens do Produto
                  <span className="tooltip-icon" title={`Selecione até ${MAX_IMAGENS} imagens (máximo 5MB cada). A primeira será a principal.`}>
                    <AppIcon name="info" />
                  </span>
                  <span className="image-count">
                    {form.imagens?.length || 0}/{MAX_IMAGENS}
                  </span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="file-input"
                  disabled={form.imagens?.length >= MAX_IMAGENS}
                />
                
                {/* ✅ Galeria de imagens */}
                {form.imagens && form.imagens.length > 0 && (
                  <div className="images-gallery">
                    {form.imagens.map((imagem, index) => (
                      <div key={index} className={`gallery-item ${index === 0 ? 'principal' : ''}`}>
                        <img src={imagem} alt={`Imagem ${index + 1}`} />
                        <div className="gallery-item-actions">
                          {index !== 0 && (
                            <button
                              type="button"
                              className="btn-set-principal"
                              onClick={() => handleSetPrincipal(index)}
                              title="Definir como principal"
                            >
                              <AppIcon name="star" />
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-remove-image"
                            onClick={() => handleRemoveImage(index)}
                            title="Remover imagem"
                          >
                            <AppIcon name="close" />
                          </button>
                        </div>
                        {index === 0 && <span className="badge-principal">Principal</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  <AppIcon name="save" />
                  Salvar Produto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Produto Gas Distributor */}
      {showModal && tipoProdutoSelecionado === 'gas_distributor' && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content product-modal" onClick={(e) => e.stopPropagation()}>
            <GasDistributorProductForm
              produto={produtoGasDistributor}
              onSave={handleSalvarGasDistributor}
              onCancel={() => setShowModal(false)}
              companyId={companyId}
              projectId={gasDistributorProjectId}
            />
          </div>
        </div>
      )}

      {/* Modal de Importação via URL */}
      {showImportModal && (
        <ProductImportModal
          companyId={companyId}
          onClose={() => setShowImportModal(false)}
          onImport={handleImportProduct}
        />
      )}
    </div>
  );
};

export default ProductsManager;
